import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormFeedback } from '@/components/ui/form-feedback';
import { Input } from '@/components/ui/input';

const oversightEntrySchema = z.object({ studyId: z.string().uuid('Deve essere un UUID valido') });
type OversightEntryForm = z.infer<typeof oversightEntrySchema>;

/**
 * `/clinical-study/oversight` (Resolution R3): the CRO monitor's own entry point, since
 * `ClinicalStudiesController.list`/`.getById` both require `clinical-study:study:manage` — a
 * permission the CRO monitor never holds (verified against the seed catalog) — leaving no other
 * in-app way to discover a Clinical Study's id. Mirrors `IdentifierCorrectionSection.tsx`'s own
 * "no resolving UI exists yet" raw-UUID precedent. On submit, navigates to
 * `/clinical-study/oversight/:studyId` — no fetch happens on this page itself.
 */
export function StudyOversightEntryPage() {
  const { t } = useTranslation('clinical-study');
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OversightEntryForm>({ resolver: zodResolver(oversightEntrySchema) });

  const onSubmit = (values: OversightEntryForm) => {
    void navigate(`/clinical-study/oversight/${values.studyId}`);
  };

  return (
    <div className="max-w-md space-y-6">
      <h2 className="screen-title">{t('oversight.entryTitle')}</h2>
      <Card>
        <CardHeader>
          <CardTitle>{t('oversight.entryFormTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={(event) => void handleSubmit(onSubmit)(event)}>
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="oversight-study-id">
                {t('oversight.studyIdLabel')}
              </label>
              <Input
                id="oversight-study-id"
                aria-invalid={errors.studyId ? true : undefined}
                aria-describedby={
                  errors.studyId
                    ? 'oversight-study-id-hint oversight-study-id-error'
                    : 'oversight-study-id-hint'
                }
                {...register('studyId')}
              />
              <p id="oversight-study-id-hint" className="text-xs text-muted-foreground">
                {t('oversight.studyIdHint')}
              </p>
              {errors.studyId && (
                <FormFeedback tone="error" id="oversight-study-id-error">
                  {errors.studyId.message}
                </FormFeedback>
              )}
            </div>
            <Button type="submit">{t('oversight.entrySubmit')}</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
